import React, { useState } from 'react';
import {
  Button,
  Modal,
  Form,
  Input,
  Select,
  Row,
  Col,
  Space,
  Tag,
  Tooltip,
  DatePicker,
  Upload,
  List,
  message,
  Checkbox,
} from 'antd';
import {
  PlusOutlined,
  TeamOutlined,
  ProfileOutlined,
  ApartmentOutlined,
  FileTextOutlined,
  BuildOutlined,
  BarcodeOutlined,
  SlidersOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';

import MyTable from '../helpers/MyTable';
import SubPageLayout from '../helpers/SubPageLayout';
import SubPageHeader from '../helpers/SubPageHeader';

const { Option } = Select;

// ---------- MASTER DATA ----------

const STAGE_DEFINITION = [
  {
    key: 'Pre-build',
    name: 'Pre-build',
    sections: [
      {
        title: null,
        items: [
          'Pre-build sign offs',
          'Build Sheet',
          'Drawings',
          'Fabrication sheet',
          'Paint Sheet',
          'Send to Fabrication',
        ],
      },
    ],
  },
  {
    key: 'Fabrication',
    name: 'Fabrication',
    sections: [
      {
        title: null,
        items: ['Fabrication inspection', 'Fabrication Sign off'],
      },
    ],
  },
  {
    key: 'Paint & Blast',
    name: 'Paint & Blast',
    sections: [
      {
        title: null,
        items: ['Paint Sign off'],
      },
    ],
  },
  {
    key: 'Materials',
    name: 'Materials',
    sections: [
      {
        title: null,
        items: ['Bill of Materials'],
      },
    ],
  },
  {
    key: 'Stickers',
    name: 'Stickers',
    sections: [
      {
        title: null,
        items: ['Print stickers'],
      },
    ],
  },
  {
    key: 'Build (Pre-Electrical)',
    name: 'Build (Pre-Electrical)',
    sections: [
      {
        title: 'Front Section / First Fix wiring',
        items: [
          'Ducting',
          'Fit all glands',
          'First-fix wiring, including GuardIAN cables',
          'Front wall and door stripe to be applied',
          'Door seal installation',
          'Port-hole window',
          '2 inch Vent valves',
          'Strobe lights and siren',
        ],
      },
      {
        title: 'Back Section',
        items: [
          'Back wall',
          'Comms box',
          'Iso switch',
          'Air-conditioning condenser external and evaporator install internal (Vac test and Gas release)',
          'Battery box fitout',
          'Cable looms',
          'Probes',
          'Floor mat',
          'Battery box doors fit-out',
        ],
      },
      {
        title: 'CAMS Installation',
        items: [
          'CAMS external install',
          'CAMS internal install (including ECU and ECU wiring)',
        ],
      },
      {
        title: 'Scrubber System',
        items: ['Install scrubber', 'Connect low-voltage wiring'],
      },
    ],
  },
  {
    key: 'Electrician Testing',
    name: 'Electrician Testing',
    sections: [
      {
        title: 'Electrical Work',
        items: [
          'Electrician to complete all electrical works on the chamber',
          'Produce electrical certificate.',
        ],
      },
    ],
  },
  {
    key: 'Build (Post-Electrical)',
    name: 'Build (Post-Electrical)',
    sections: [
      {
        title: 'Interior Fit-Out',
        items: [
          'Seats',
          'Floor mat',
          'Toilet curtain',
          'Gas cylinder brackets',
          'Other internal accessories',
        ],
      },
      {
        title: 'External / Internal Finishing',
        items: [
          'Operational stickers',
          'External green stripe',
          'Final external and internal markings',
        ],
      },
      {
        title: 'Commissioning',
        items: [
          'Load chemicals and consumables',
          'Clean and tidy chamber',
          'Set parameters on scrubber',
          'Carry out vacuum test on chamber',
        ],
      },
    ],
  },
  {
    key: 'Approvals',
    name: 'Approvals',
    sections: [
      {
        title: null,
        items: [
          'Approval Check list',
          'Two Supervisors from workshop',
          'Sales BDM sign off',
        ],
      },
    ],
  },
  {
    key: 'Shipping',
    name: 'Shipping',
    sections: [
      {
        title: null,
        items: ['Despatch', 'Shipping photos'],
      },
    ],
  },
];

const defaultStageOrder = STAGE_DEFINITION.map((s) => s.key);

const technicianOptions = [
  'John Smith',
  'Lerato Khumalo',
  'Peter Jones',
  'Emily Davis',
  'Carlos Martinez',
];

const contractorOptions = [
  'MineARC Fabrication',
  'SteelWorks Pty Ltd',
  'Global Steel Solutions',
  'SafeBuild Contractors',
  'StormFab Engineering',
  'SurfaceCoat Solutions',
  'PrimeBlast Services',
  'Premium Blast & Paint',
  'DeepMine Coatings',
];

const languageOptions = [
  'English',
  'Afrikaans',
  'French',
  'Portuguese',
  'Spanish',
  'Swahili',
  'Zulu',
];

// Demo products “that passed Pre-build”
const initialProducts = [
  {
    id: 1,
    image: '/product_images/MineSAFE_Modular_Design.png',
    productName: 'MineSAFE Modular Design',
    productCode: 'MineSAFE_Modular_Design',
    serialNumber: 'MAF1236',
    technicians: ['John Smith', 'Lerato Khumalo'],
    companyName: 'Apex Mining Ltd',
    businessManager: 'Sarah Johnson',
    currentStage: 'Fabrication',
    preBuildComplete: true,
    stageOrder: [...defaultStageOrder],
    fabricator: 'MineARC Fabrication',
    paintBlast: 'SurfaceCoat Solutions',
    drawings: [
      { id: 1, name: 'General Arrangement.pdf', url: '/docs/GA_1236.pdf' },
      { id: 2, name: 'P&ID.pdf', url: '/docs/PID_1236.pdf' },
      { id: 3, name: 'Electrical Layout.pdf', url: '/docs/ELEC_1236.pdf' },
    ],
    bom: [
      { id: 1, item: 'Structural Steel', qty: 120, unit: 'm' },
      { id: 2, item: 'Paint – Safety Yellow', qty: 40, unit: 'L' },
    ],
    stickersConfig: {
      stickers: ['Product Name stickers', 'Gas Sensors table stickers'],
      language: 'English',
    },
    productionPaused: false,
  },
  {
    id: 2,
    image: '/product_images/ChemSAFE_Standard_Design.png',
    productName: 'ChemSAFE Standard Design',
    productCode: 'ChemSAFE_Standard_Design',
    serialNumber: 'MAF1237',
    technicians: ['Peter Jones'],
    companyName: 'SafeChem Industries',
    businessManager: 'Michael Chen',
    currentStage: 'Materials',
    preBuildComplete: true,
    stageOrder: [...defaultStageOrder],
    fabricator: 'SteelWorks Pty Ltd',
    paintBlast: 'PrimeBlast Services',
    drawings: [],
    bom: [],
    stickersConfig: {
      stickers: [],
      language: 'English',
    },
    productionPaused: false,
  },
];

// ---------- MAIN COMPONENT ----------

const ProductionPlanning = () => {
  const [products, setProducts] = useState(initialProducts);
  const [searchQuery, setSearchQuery] = useState('');

  const [activeModal, setActiveModal] = useState(null); // 'technicians' | 'buildSheet' | 'contractors' | 'drawings' | 'bom' | 'stickers' | 'production'
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [stageOrderDraft, setStageOrderDraft] = useState(defaultStageOrder);
  const [bomDraft, setBomDraft] = useState([]);
  const [stickersDraft, setStickersDraft] = useState({
    stickers: [],
    language: 'English',
  });

  const [techForm] = Form.useForm();
  const [contractorForm] = Form.useForm();

  // ---------- HELPERS ----------

  const openModal = (type, product) => {
    setSelectedProduct(product);

    if (type === 'technicians') {
      techForm.setFieldsValue({
        technicians: product.technicians || [],
      });
    }

    if (type === 'contractors') {
      contractorForm.setFieldsValue({
        fabricator: product.fabricator || undefined,
        paintBlast: product.paintBlast || undefined,
        fabEta: product.fabEta || null,
        paintEta: product.paintEta || null,
      });
    }

    if (type === 'buildSheet') {
      setStageOrderDraft(product.stageOrder || defaultStageOrder);
    }

    if (type === 'bom') {
      setBomDraft(product.bom && product.bom.length ? product.bom : []);
    }

    if (type === 'stickers') {
      setStickersDraft(
        product.stickersConfig || {
          stickers: [],
          language: 'English',
        }
      );
    }

    setActiveModal(type);
  };

  const closeModal = () => {
    setActiveModal(null);
    setSelectedProduct(null);
  };

  const updateProduct = (id, patch) => {
    setProducts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, ...patch } : p))
    );
    if (selectedProduct && selectedProduct.id === id) {
      setSelectedProduct((prev) => ({ ...prev, ...patch }));
    }
  };

  // Drag handlers for build sheet stage order
  const moveStage = (fromIndex, toIndex) => {
    setStageOrderDraft((prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(fromIndex, 1);
      arr.splice(toIndex, 0, moved);
      return arr;
    });
  };

  const handleStageDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleStageDrop = (e, index) => {
    const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (Number.isNaN(fromIndex) || fromIndex === index) return;
    moveStage(fromIndex, index);
  };

  // ---------- FILTERED DATA ----------

  const filteredProducts = products.filter((p) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (p.productName || '').toLowerCase().includes(q) ||
      (p.serialNumber || '').toLowerCase().includes(q) ||
      (p.companyName || '').toLowerCase().includes(q) ||
      (p.technicians || []).some((t) => t.toLowerCase().includes(q))
    );
  });

  // ---------- TABLE COLUMNS ----------

  const columns = [
    {
      title: '',
      dataIndex: 'image',
      key: 'image',
      width: 90,
      render: (src, record) => (
        <div
          style={{
            width: 80,
            height: 80,
            borderRadius: 12,
            overflow: 'hidden',
            background: '#f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={src}
            alt={record.productName}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </div>
      ),
    },
    {
      title: 'Product',
      dataIndex: 'productName',
      key: 'productName',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{text}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            {record.productCode}
          </div>
        </div>
      ),
    },
    {
      title: 'Technicians',
      dataIndex: 'technicians',
      key: 'technicians',
      render: (techs) =>
        (techs || []).length ? (
          <Space size={4} wrap>
            {techs.map((t) => (
              <Tag key={t} color="blue">
                {t}
              </Tag>
            ))}
          </Space>
        ) : (
          <span style={{ fontSize: 12, color: '#9ca3af' }}>Unassigned</span>
        ),
    },
    {
      title: 'Serial',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
    },
    {
      title: 'Stage',
      dataIndex: 'currentStage',
      key: 'currentStage',
      render: (stage) => <Tag>{stage}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const btnStyle = {
          height: 26,
          fontSize: 11,
          padding: '0 8px',
          borderRadius: 999,
        };

        return (
          <Space size="small" wrap>
            <Button
              size="small"
              style={btnStyle}
              icon={<TeamOutlined />}
              onClick={() => openModal('technicians', record)}
            >
              Assign Technician
            </Button>
            <Button
              size="small"
              style={btnStyle}
              icon={<ProfileOutlined />}
              onClick={() => openModal('buildSheet', record)}
            >
              Build Sheet
            </Button>
            <Button
              size="small"
              style={btnStyle}
              icon={<ApartmentOutlined />}
              onClick={() => openModal('contractors', record)}
            >
              Contractors
            </Button>
            <Button
              size="small"
              style={btnStyle}
              icon={<FileTextOutlined />}
              onClick={() => openModal('drawings', record)}
            >
              Drawings
            </Button>
            <Button
              size="small"
              style={btnStyle}
              icon={<SlidersOutlined />}
              onClick={() => openModal('bom', record)}
            >
              BOM
            </Button>
            <Button
              size="small"
              style={btnStyle}
              icon={<BarcodeOutlined />}
              onClick={() => openModal('stickers', record)}
            >
              Stickers
            </Button>
            <Button
              size="small"
              style={btnStyle}
              icon={
                record.productionPaused ? <PlayCircleOutlined /> : <BuildOutlined />
              }
              onClick={() => openModal('production', record)}
            >
              Production
            </Button>
          </Space>
        );
      },
    },
  ];

  // ---------- MODAL CONTENTS ----------

  const renderProductHeaderMini = () => {
    if (!selectedProduct) return null;
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 12,
            overflow: 'hidden',
            background: '#f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={selectedProduct.image}
            alt={selectedProduct.productName}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
            }}
          />
        </div>
        <div>
          <div style={{ fontWeight: 600 }}>{selectedProduct.productName}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            {selectedProduct.serialNumber} &mdash;{' '}
            {selectedProduct.companyName}
          </div>
        </div>
      </div>
    );
  };

  // Assign Technician submit
  const handleSaveTechnicians = (values) => {
    if (!selectedProduct) return;
    updateProduct(selectedProduct.id, { technicians: values.technicians || [] });
    message.success('Technicians updated');
    closeModal();
  };

  // Save build sheet stage order
  const handleSaveBuildSheetOrder = () => {
    if (!selectedProduct) return;
    updateProduct(selectedProduct.id, { stageOrder: stageOrderDraft });
    message.success('Build sheet order updated');
    closeModal();
  };

  const handleSaveContractors = (values) => {
    if (!selectedProduct) return;
    updateProduct(selectedProduct.id, {
      fabricator: values.fabricator,
      paintBlast: values.paintBlast,
      fabEta: values.fabEta ? values.fabEta.format('YYYY-MM-DD') : null,
      paintEta: values.paintEta ? values.paintEta.format('YYYY-MM-DD') : null,
    });
    message.success('Contractor assignments updated');
    closeModal();
  };

  const handleDrawingsUpload = ({ file }) => {
    if (!selectedProduct) return false;
    const newDoc = {
      id: Date.now(),
      name: file.name,
      url: URL.createObjectURL(file),
    };
    updateProduct(selectedProduct.id, {
      drawings: [...(selectedProduct.drawings || []), newDoc],
    });
    message.success('Drawing uploaded');
    return false; // prevent auto upload
  };

  const handleSaveBom = () => {
    if (!selectedProduct) return;
    updateProduct(selectedProduct.id, { bom: bomDraft });
    message.success('BOM saved');
    closeModal();
  };

  const handleAddBomRow = () => {
    setBomDraft((prev) => [
      ...prev,
      { id: Date.now(), item: '', qty: 0, unit: '' },
    ]);
  };

  const handleChangeBomRow = (id, patch) => {
    setBomDraft((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const handleRemoveBomRow = (id) => {
    setBomDraft((prev) => prev.filter((r) => r.id !== id));
  };

  const handleSaveStickers = () => {
    if (!selectedProduct) return;
    updateProduct(selectedProduct.id, { stickersConfig: stickersDraft });
    message.success('Sticker configuration saved');
    closeModal();
  };

  const handleProductionAction = (action) => {
    if (!selectedProduct) return;
    if (action === 'pauseToggle') {
      const paused = !selectedProduct.productionPaused;
      updateProduct(selectedProduct.id, { productionPaused: paused });
      message.success(
        paused ? 'Production paused for this unit' : 'Production resumed'
      );
    } else if (action === 'toFabrication') {
      updateProduct(selectedProduct.id, { currentStage: 'Fabrication' });
      message.success('Product sent to Fabrication');
    } else if (action === 'toPaint') {
      updateProduct(selectedProduct.id, { currentStage: 'Paint & Blast' });
      message.success('Product sent to Paint & Blast');
    }
    closeModal();
  };

  // ---------- RENDER ----------

  return (
    <SubPageLayout
      title="Production Planning"
      extra={
        <Tooltip title="Products that have passed Pre-build and are ready to be scheduled">
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            Planning board
          </span>
        </Tooltip>
      }
    >
      <SubPageHeader
        searchLoading={false}
        searchPlaceholder="Search products, serials or technicians"
        onSearch={(value) => setSearchQuery(value)}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      <MyTable columns={columns} dataSource={filteredProducts} rowKey="id" />

      {/* ---------- ASSIGN TECHNICIAN MODAL ---------- */}
      <Modal
        visible={activeModal === 'technicians'}
        onCancel={closeModal}
        footer={null}
        centered
        width={640}
        destroyOnClose
        title={null}
      >
        <div
          style={{
            padding: 24,
            borderRadius: 16,
            background:
              'linear-gradient(135deg, #ffffff 0%, #f3f4ff 40%, #eef7ff 100%)',
            boxShadow: '0 18px 45px rgba(15,23,42,0.18)',
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                fontSize: 20,
                fontWeight: 600,
                color: '#111827',
                marginBottom: 4,
              }}
            >
              Assign Technicians
            </div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>
              Choose one or more technicians responsible for this build.
            </div>
          </div>

          {renderProductHeaderMini()}

          <Form
            form={techForm}
            layout="vertical"
            onFinish={handleSaveTechnicians}
          >
            <Form.Item
              label="Technicians"
              name="technicians"
              rules={[
                {
                  required: true,
                  message: 'Please select at least one technician',
                },
              ]}
            >
              <Select
                mode="tags"
                placeholder="Select or type technician names"
              >
                {technicianOptions.map((t) => (
                  <Option key={t} value={t}>
                    {t}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
                marginTop: 8,
              }}
            >
              <Button onClick={closeModal}>Cancel</Button>
              <Button
                type="primary"
                icon={<TeamOutlined />}
                onClick={() => techForm.submit()}
              >
                Save Technicians
              </Button>
            </div>
          </Form>
        </div>
      </Modal>

      {/* ---------- BUILD SHEET MODAL ---------- */}
      <Modal
        visible={activeModal === 'buildSheet'}
        onCancel={closeModal}
        footer={null}
        centered
        width={960}
        destroyOnClose
        title={null}
      >
        <div
          style={{
            padding: 24,
            borderRadius: 16,
            background:
              'linear-gradient(135deg, #ffffff 0%, #f9fafb 40%, #eef2ff 100%)',
            boxShadow: '0 18px 45px rgba(15,23,42,0.18)',
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                fontSize: 20,
                fontWeight: 600,
                color: '#111827',
                marginBottom: 4,
              }}
            >
              Build Sheet & Production Stages
            </div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>
              Drag or use the arrows to re-order the 10 production stages for
              this product. Tasks within each stage are listed beneath.
            </div>
          </div>

          {renderProductHeaderMini()}

          <Row gutter={24}>
            <Col span={10}>
              <List
                dataSource={stageOrderDraft}
                renderItem={(stageKey, index) => {
                  const stage = STAGE_DEFINITION.find(
                    (s) => s.key === stageKey
                  );
                  if (!stage) return null;
                  return (
                    <List.Item
                      key={stage.key}
                      draggable
                      onDragStart={(e) => handleStageDragStart(e, index)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => handleStageDrop(e, index)}
                      style={{
                        borderRadius: 10,
                        padding: '10px 12px',
                        marginBottom: 8,
                        background: '#f3f4ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'grab',
                      }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 500 }}>
                        {index + 1}. {stage.name}
                      </span>
                      <Space size={0}>
                        <Tooltip title="Move up">
                          <Button
                            type="text"
                            size="small"
                            icon={<ArrowUpOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (index > 0) moveStage(index, index - 1);
                            }}
                          />
                        </Tooltip>
                        <Tooltip title="Move down">
                          <Button
                            type="text"
                            size="small"
                            icon={<ArrowDownOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (index < stageOrderDraft.length - 1)
                                moveStage(index, index + 1);
                            }}
                          />
                        </Tooltip>
                      </Space>
                    </List.Item>
                  );
                }}
              />
            </Col>
            <Col span={14}>
              <div
                style={{
                  maxHeight: 380,
                  overflowY: 'auto',
                  padding: '0 4px',
                }}
              >
                {stageOrderDraft.map((stageKey, index) => {
                  const stage = STAGE_DEFINITION.find(
                    (s) => s.key === stageKey
                  );
                  if (!stage) return null;
                  return (
                    <div
                      key={stage.key}
                      style={{
                        marginBottom: 14,
                        paddingBottom: 10,
                        borderBottom: '1px solid #e5e7eb',
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: 13,
                          marginBottom: 4,
                        }}
                      >
                        {index + 1}. {stage.name}
                      </div>
                      {stage.sections.map((section, i) => (
                        <div key={section.title || i} style={{ marginBottom: 6 }}>
                          {section.title && (
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 500,
                                color: '#4b5563',
                                marginBottom: 2,
                              }}
                            >
                              {section.title}
                            </div>
                          )}
                          <ul
                            style={{
                              paddingLeft: 18,
                              marginBottom: 0,
                              fontSize: 12,
                            }}
                          >
                            {section.items.map((t) => (
                              <li key={t}>{t}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </Col>
          </Row>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              marginTop: 16,
            }}
          >
            <Button onClick={closeModal}>Cancel</Button>
            <Button
              type="primary"
              icon={<ProfileOutlined />}
              onClick={handleSaveBuildSheetOrder}
            >
              Save Build Sheet
            </Button>
          </div>
        </div>
      </Modal>

      {/* ---------- CONTRACTORS MODAL ---------- */}
      <Modal
        visible={activeModal === 'contractors'}
        onCancel={closeModal}
        footer={null}
        centered
        width={720}
        destroyOnClose
        title={null}
      >
        <div
          style={{
            padding: 24,
            borderRadius: 16,
            background:
              'linear-gradient(135deg, #ffffff 0%, #f3f4ff 40%, #eef7ff 100%)',
            boxShadow: '0 18px 45px rgba(15,23,42,0.18)',
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                fontSize: 20,
                fontWeight: 600,
                color: '#111827',
                marginBottom: 4,
              }}
            >
              Contractors & Delivery
            </div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>
              Assign fabrication and paint contractors, and capture their
              expected delivery dates.
            </div>
          </div>

          {renderProductHeaderMini()}

          <Form
            layout="vertical"
            form={contractorForm}
            onFinish={handleSaveContractors}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Fabricator"
                  name="fabricator"
                  rules={[
                    { required: true, message: 'Please select a fabricator' },
                  ]}
                >
                  <Select placeholder="Select fabricator">
                    {contractorOptions.map((c) => (
                      <Option key={c} value={c}>
                        {c}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Fabrication ETA" name="fabEta">
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Paint & Blast Contractor"
                  name="paintBlast"
                  rules={[
                    {
                      required: true,
                      message: 'Please select a Paint & Blast contractor',
                    },
                  ]}
                >
                  <Select placeholder="Select contractor">
                    {contractorOptions.map((c) => (
                      <Option key={c} value={c}>
                        {c}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Paint & Blast ETA" name="paintEta">
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
                marginTop: 8,
              }}
            >
              <Button onClick={closeModal}>Cancel</Button>
              <Button
                type="primary"
                icon={<ApartmentOutlined />}
                onClick={() => contractorForm.submit()}
              >
                Save Contractors
              </Button>
            </div>
          </Form>
        </div>
      </Modal>

      {/* ---------- DRAWINGS MODAL ---------- */}
      <Modal
        visible={activeModal === 'drawings'}
        onCancel={closeModal}
        footer={null}
        centered
        width={720}
        destroyOnClose
        title={null}
      >
        <div
          style={{
            padding: 24,
            borderRadius: 16,
            background: '#ffffff',
            boxShadow: '0 18px 45px rgba(15,23,42,0.18)',
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                fontSize: 20,
                fontWeight: 600,
                color: '#111827',
                marginBottom: 4,
              }}
            >
              Drawings
            </div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>
              Access key PDFs for this product or upload missing drawings.
            </div>
          </div>

          {renderProductHeaderMini()}

          <List
            dataSource={selectedProduct?.drawings || []}
            locale={{ emptyText: 'No drawings uploaded yet.' }}
            renderItem={(doc) => (
              <List.Item key={doc.id}>
                <Space>
                  <FilePdfOutlined style={{ fontSize: 20, color: '#ef4444' }} />
                  {doc.url ? (
                    <a href={doc.url} target="_blank" rel="noopener noreferrer">
                      {doc.name}
                    </a>
                  ) : (
                    <span>{doc.name}</span>
                  )}
                </Space>
              </List.Item>
            )}
          />

          <div style={{ marginTop: 16 }}>
            <Upload.Dragger
              multiple={false}
              beforeUpload={(file) => handleDrawingsUpload({ file })}
              accept="application/pdf"
              style={{
                borderRadius: 12,
                background: '#f9fafb',
              }}
            >
              <p className="ant-upload-drag-icon">
                <FilePdfOutlined style={{ fontSize: 32, color: '#ef4444' }} />
              </p>
              <p className="ant-upload-text">
                Click or drag PDF here to upload a drawing
              </p>
              <p className="ant-upload-hint" style={{ fontSize: 12 }}>
                Upload GA, P&amp;ID, electrical layouts or any other relevant
                drawing in PDF format.
              </p>
            </Upload.Dragger>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 12,
            }}
          >
            <Button onClick={closeModal}>Close</Button>
          </div>
        </div>
      </Modal>

      {/* ---------- BOM MODAL ---------- */}
      <Modal
        visible={activeModal === 'bom'}
        onCancel={closeModal}
        footer={null}
        centered
        width={820}
        destroyOnClose
        title={null}
      >
        <div
          style={{
            padding: 24,
            borderRadius: 16,
            background: '#ffffff',
            boxShadow: '0 18px 45px rgba(15,23,42,0.18)',
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                fontSize: 20,
                fontWeight: 600,
                color: '#111827',
                marginBottom: 4,
              }}
            >
              Bill of Materials
            </div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>
              Capture the key materials and quantities required for this unit.
            </div>
          </div>

          {renderProductHeaderMini()}

          <div
            style={{
              maxHeight: 360,
              overflowY: 'auto',
              borderRadius: 12,
              border: '1px solid #e5e7eb',
              padding: 12,
              marginBottom: 12,
            }}
          >
            {bomDraft.length === 0 && (
              <div
                style={{
                  fontSize: 13,
                  color: '#9ca3af',
                  textAlign: 'center',
                  padding: '40px 0',
                }}
              >
                No BOM items captured yet.
              </div>
            )}

            {bomDraft.map((row) => (
              <Row
                key={row.id}
                gutter={8}
                style={{ marginBottom: 8, alignItems: 'center' }}
              >
                <Col span={12}>
                  <Input
                    placeholder="Item / Description"
                    value={row.item}
                    onChange={(e) =>
                      handleChangeBomRow(row.id, { item: e.target.value })
                    }
                  />
                </Col>
                <Col span={6}>
                  <Input
                    placeholder="Quantity"
                    type="number"
                    value={row.qty}
                    onChange={(e) =>
                      handleChangeBomRow(row.id, { qty: e.target.value })
                    }
                  />
                </Col>
                <Col span={4}>
                  <Input
                    placeholder="Unit"
                    value={row.unit}
                    onChange={(e) =>
                      handleChangeBomRow(row.id, { unit: e.target.value })
                    }
                  />
                </Col>
                <Col span={2}>
                  <Button
                    type="text"
                    danger
                    onClick={() => handleRemoveBomRow(row.id)}
                  >
                    Remove
                  </Button>
                </Col>
              </Row>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button icon={<PlusOutlined />} onClick={handleAddBomRow}>
              Add Item
            </Button>
            <Space>
              <Button onClick={closeModal}>Cancel</Button>
              <Button
                type="primary"
                icon={<SlidersOutlined />}
                onClick={handleSaveBom}
              >
                Save BOM
              </Button>
            </Space>
          </div>
        </div>
      </Modal>

      {/* ---------- STICKERS MODAL ---------- */}
      <Modal
        visible={activeModal === 'stickers'}
        onCancel={closeModal}
        footer={null}
        centered
        width={640}
        destroyOnClose
        title={null}
      >
        <div
          style={{
            padding: 24,
            borderRadius: 16,
            background: '#ffffff',
            boxShadow: '0 18px 45px rgba(15,23,42,0.18)',
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                fontSize: 20,
                fontWeight: 600,
                color: '#111827',
                marginBottom: 4,
              }}
            >
              Stickers & Language
            </div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>
              Choose which sticker sets to print and the primary language.
            </div>
          </div>

          {renderProductHeaderMini()}

          <Row gutter={16}>
            <Col span={14}>
              <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 13 }}>
                Sticker Sets
              </div>
              <Space direction="vertical">
                {[
                  'Product Name stickers',
                  'Gas Sensors table stickers',
                  'Scrubber stickers',
                  'External stickers',
                  'Indicators stickers',
                ].map((label) => (
                  <Checkbox
                    key={label}
                    checked={stickersDraft.stickers.includes(label)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setStickersDraft((prev) => {
                        const set = new Set(prev.stickers);
                        if (checked) set.add(label);
                        else set.delete(label);
                        return { ...prev, stickers: Array.from(set) };
                      });
                    }}
                  >
                    {label}
                  </Checkbox>
                ))}
              </Space>
            </Col>
            <Col span={10}>
              <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 13 }}>
                Language
              </div>
              <Select
                value={stickersDraft.language}
                onChange={(val) =>
                  setStickersDraft((prev) => ({ ...prev, language: val }))
                }
                style={{ width: '100%' }}
              >
                {languageOptions.map((lang) => (
                  <Option key={lang} value={lang}>
                    {lang}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              marginTop: 16,
            }}
          >
            <Button onClick={closeModal}>Cancel</Button>
            <Button
              type="primary"
              icon={<BarcodeOutlined />}
              onClick={handleSaveStickers}
            >
              Save Sticker Setup
            </Button>
          </div>
        </div>
      </Modal>

      {/* ---------- PRODUCTION ACTIONS MODAL ---------- */}
      <Modal
        visible={activeModal === 'production'}
        onCancel={closeModal}
        footer={null}
        centered
        width={520}
        destroyOnClose
        title={null}
      >
        <div
          style={{
            padding: 24,
            borderRadius: 16,
            background:
              'linear-gradient(135deg, #ffffff 0%, #f9fafb 40%, #e0f2fe 100%)',
            boxShadow: '0 18px 45px rgba(15,23,42,0.18)',
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                fontSize: 20,
                fontWeight: 600,
                color: '#111827',
                marginBottom: 4,
              }}
            >
              Production Actions
            </div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>
              Move this product between key workshop stages or pause its build.
            </div>
          </div>

          {renderProductHeaderMini()}

          <Space direction="vertical" style={{ width: '100%' }}>
            <Button
              block
              icon={<BuildOutlined />}
              onClick={() => handleProductionAction('toFabrication')}
            >
              Send Product to Fabrication
            </Button>
            <Button
              block
              icon={<ApartmentOutlined />}
              onClick={() => handleProductionAction('toPaint')}
            >
              Send Product to Paint &amp; Blast
            </Button>
            <Button
              block
              type={selectedProduct?.productionPaused ? 'primary' : 'default'}
              icon={
                selectedProduct?.productionPaused ? (
                  <PlayCircleOutlined />
                ) : (
                  <PauseCircleOutlined />
                )
              }
              onClick={() => handleProductionAction('pauseToggle')}
            >
              {selectedProduct?.productionPaused
                ? 'Resume Production'
                : 'Pause Production'}
            </Button>
          </Space>

          <div
            style={{
              fontSize: 12,
              color: '#6b7280',
              marginTop: 12,
            }}
          >
            Tip: These actions will update the product&apos;s current stage and
            status across the planning and timeline views.
          </div>
        </div>
      </Modal>
    </SubPageLayout>
  );
};

export default ProductionPlanning;
